//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { PrimaryRouteAnchor } from '@components/RouteAnchor';
import { BodyText, SubsectionText } from '@components/atoms/text/Text';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { UiaProductGrid } from '@uia/products/UiaProductGrid';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';

export interface UiaTagProductsProps {
  handlePageChange: (currentPage: number, itemsPerPage: number) => void;
  hasCatalogConnected: boolean;
  isLoadingProducts: boolean;
  limit: number;
  offset: number;
  products: CuralateCatalogProduct[] | undefined;
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
}

export const UiaTagProducts = ({
  handlePageChange,
  hasCatalogConnected,
  isLoadingProducts,
  limit,
  offset,
  products,
  searchQuery,
  setSearchQuery,
}: UiaTagProductsProps) => {
  const tagSingleProduct = async (product: CuralateCatalogProduct) => {
    // TODO: PD-284077 - tag a product
  };

  return (
    <>
      <BodyText>Search Product or Enter URL</BodyText>
      <SearchBar
        onChange={setSearchQuery}
        placeholder='Product name, SKU, or URL'
        trackingIdPrefix={UiaProductTrackingIds.PRODUCT_SEARCH_BAR}
        value={searchQuery}
        visuallyHiddenLabel='Product Search'
      />
      {hasCatalogConnected ? (
        <UiaProductGrid
          handlePageChange={handlePageChange}
          isLoadingProducts={isLoadingProducts}
          limit={limit}
          offset={offset}
          productsResults={products}
          searchQuery={searchQuery}
          tagSingleProduct={tagSingleProduct}
        />
      ) : (
        <NoCatalogContainer>
          <SubsectionText>Your product catalog isn’t connected</SubsectionText>
          <BodyText>
            Bazaarvoice uses catalog data like price, category, and availability
            to turn discovery into commerce from your touch points.
          </BodyText>
          <BodyText>
            <PrimaryRouteAnchor state={'account-settings.ecommerceSetup'}>
              Connect your product catalog
            </PrimaryRouteAnchor>{' '}
            so we can help you monetize your media at scale, boost
            discoverability, and increase conversions.
          </BodyText>
          <BodyText>
            Alternatively, you can also tag a product by entering a URL above.
          </BodyText>
        </NoCatalogContainer>
      )}
    </>
  );
};

const NoCatalogContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[2]};
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: ${theme.spacings[3]};
    background-color: ${theme.colors.gray00};
  `,
);
